import { reachGoal } from './metrika'
import type { ToolProcessingMode } from './toolEvents'

export const PORTFOLIO_CROSS_LINK_GOAL = 'portfolio.cross_link.click'

export type PortfolioCrossLinkSurface =
  | 'portfolio_nav'
  | 'related_tools'
  | 'inline_cta'
  | 'catalog_card'
  | 'blog_related'

export type PortfolioCrossLinkReason = 'same_user_job' | 'adjacent_format' | 'generic_nav'

export interface PortfolioCrossLinkContext {
  sourceApp: string
  sourceDomain: string
  sourceRoute: string
  sourceSurface: PortfolioCrossLinkSurface
  targetApp: string
  targetDomain: string
  targetRoute: string
  linkReason: PortfolioCrossLinkReason
  targetUrl: string
  sourceToolKey?: string
  targetToolKey?: string
  sourceAnalyticsKey?: string
  targetAnalyticsKey?: string
  position?: number
  locale?: string
  processingModeSource?: ToolProcessingMode
  processingModeTarget?: ToolProcessingMode
}

export interface PortfolioCrossLinkPayload extends Record<string, unknown> {
  source_app: string
  source_domain: string
  source_route: string
  source_surface: PortfolioCrossLinkSurface
  target_app: string
  target_domain: string
  target_route: string
  link_reason: PortfolioCrossLinkReason
  target_url: string
  source_tool_key?: string
  target_tool_key?: string
  source_analytics_key?: string
  target_analytics_key?: string
  position?: number
  locale?: string
  processing_mode_source?: ToolProcessingMode
  processing_mode_target?: ToolProcessingMode
}

const trackedClicks = new WeakSet<Event>()

export function buildPortfolioCrossLinkPayload(
  context: PortfolioCrossLinkContext,
): PortfolioCrossLinkPayload {
  return {
    source_app: context.sourceApp,
    source_domain: context.sourceDomain,
    source_route: canonicalPath(context.sourceRoute, context.sourceDomain),
    source_surface: context.sourceSurface,
    target_app: context.targetApp,
    target_domain: context.targetDomain,
    target_route: canonicalPath(context.targetRoute, context.targetDomain),
    link_reason: context.linkReason,
    target_url: withoutQueryOrHash(context.targetUrl, context.targetDomain),
    ...(context.sourceToolKey === undefined ? {} : { source_tool_key: context.sourceToolKey }),
    ...(context.targetToolKey === undefined ? {} : { target_tool_key: context.targetToolKey }),
    ...(context.sourceAnalyticsKey === undefined
      ? {}
      : { source_analytics_key: context.sourceAnalyticsKey }),
    ...(context.targetAnalyticsKey === undefined
      ? {}
      : { target_analytics_key: context.targetAnalyticsKey }),
    ...(context.position === undefined ? {} : { position: context.position }),
    ...(context.locale === undefined ? {} : { locale: context.locale }),
    ...(context.processingModeSource === undefined
      ? {}
      : { processing_mode_source: context.processingModeSource }),
    ...(context.processingModeTarget === undefined
      ? {}
      : { processing_mode_target: context.processingModeTarget }),
  }
}

export function trackPortfolioCrossLink(
  context: PortfolioCrossLinkContext,
  clickEvent?: Event,
): void {
  try {
    if (clickEvent) {
      if (trackedClicks.has(clickEvent)) return
      trackedClicks.add(clickEvent)
    }

    reachGoal(PORTFOLIO_CROSS_LINK_GOAL, buildPortfolioCrossLinkPayload(context))
  } catch {
    // Analytics must never interrupt ordinary anchor navigation.
  }
}

function withoutQueryOrHash(targetUrl: string, targetDomain: string): string {
  const url = new URL(targetUrl, `https://${targetDomain}`)
  return `${url.origin}${url.pathname}`
}

function canonicalPath(value: string, domain: string): string {
  return new URL(value, `https://${domain}`).pathname
}
